/**
 * Local IN_PROGRESS overlay for the gap between POST /findings and the next
 * file-info read that carries ``manualObfuscationJobStatus``.
 */

type Listener = () => void;

type PendingJob = {
  /** Bumps on every new flag so the blur poll cannot reuse a finished result. */
  generation: number;
  /**
   * True when this flag started against a file-info row that was already
   * REVIEWED. A verifier clear during an in-progress blur is not that case.
   */
  baselineReviewed: boolean;
};

const pendingJobs = new Map<string, PendingJob>();
/** Monotonic per file. Clearing the job must not reuse an older poll cache. */
const generations = new Map<string, number>();
const listeners = new Set<Listener>();

function normalizeUrl(url: string): string {
  return url.trim().replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
}

function notify(): void {
  listeners.forEach((fn) => fn());
}

export function subscribeManualObfuscationJobs(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function markManualObfuscationInProgress(
  fileUrl: string,
  baselineReviewed = false,
): void {
  const key = normalizeUrl(fileUrl);
  if (!key) return;
  const generation = (generations.get(key) ?? 0) + 1;
  generations.set(key, generation);
  pendingJobs.set(key, { generation, baselineReviewed });
  notify();
}

export function clearManualObfuscationJob(fileUrl: string): void {
  const key = normalizeUrl(fileUrl);
  if (!key || !pendingJobs.has(key)) return;
  pendingJobs.delete(key);
  notify();
}

export function isLocalManualObfuscationInProgress(fileUrl?: string | null): boolean {
  if (!fileUrl) return false;
  return pendingJobs.has(normalizeUrl(fileUrl));
}

export function obfuscationJobGeneration(fileUrl?: string | null): number {
  if (!fileUrl) return 0;
  return generations.get(normalizeUrl(fileUrl)) ?? 0;
}

export function isLocalObfuscationBaselineReviewed(fileUrl?: string | null): boolean {
  if (!fileUrl) return false;
  return pendingJobs.get(normalizeUrl(fileUrl))?.baselineReviewed ?? false;
}
